// 요약 지표 차트 클릭 → 항목별 목록 사이드패널 (기존 누적 현황과 같은 목록 패널을 쓴다)
// 목록 행을 누르면 상세 패널이 목록 위에 열린다.

import { useCallback, useState } from 'react'
import TaskItemsListPanel from '../../DetailDashboard/components/TaskItemsListPanel'
import TasksListPanel from '../../DetailDashboard/components/TasksListPanel'
import TaskBudgetsListPanel from '../../DetailDashboard/components/TaskBudgetsListPanel'
import TaskPerformancesListPanel from '../../DetailDashboard/components/TaskPerformancesListPanel'
import TaskDetailPanel from '@/pages/project/TaskManagement/components/TaskDetailPanel'
import type { TaskTreeNode } from '@/pages/project/TaskManagement/components/TaskHierarchyTable'
import type { MetricKey } from '../strategyMetrics'

/** 열 목록: 지표 종류 + 사업·기간 필터 (필터는 패널에서 잠긴다) */
export interface MetricListRequest {
  metricKey: MetricKey
  title: string
  businessId?: string
  businessName?: string
  startDate: Date
  endDate: Date
}

interface MetricListPanelsProps {
  workspaceId: string
  request: MetricListRequest | null
  onClose: () => void
}

export default function MetricListPanels({
  workspaceId,
  request,
  onClose,
}: MetricListPanelsProps) {
  // 상세 패널: 수행업무는 task_item, 나머지(세부실행과제·예산·성과)는 세부실행과제 상세
  const [detail, setDetail] = useState<{
    id: string
    type: 'task' | 'task_item'
  } | null>(null)

  const openTask = useCallback((taskId: string) => {
    if (taskId) setDetail({ id: taskId, type: 'task' })
  }, [])
  const openTaskNode = useCallback(
    (node: TaskTreeNode) => {
      if (node?.id) openTask(node.id)
    },
    [openTask]
  )
  const openTaskItemNode = useCallback((node: TaskTreeNode) => {
    if (node?.id) setDetail({ id: node.id, type: 'task_item' })
  }, [])

  const commonProps = {
    onClose,
    workspaceId,
    selectedBusinessId: request?.businessId,
    selectedBusinessName: request?.businessName,
    startDate: request?.startDate ?? null,
    endDate: request?.endDate ?? null,
  }
  const titleFor = (key: MetricKey, fallback: string) =>
    request?.metricKey === key ? request.title : fallback

  return (
    <>
      <TasksListPanel
        {...commonProps}
        isOpen={request?.metricKey === 'task'}
        title={titleFor('task', '세부실행과제 목록')}
        onTaskClick={openTaskNode}
      />
      <TaskItemsListPanel
        {...commonProps}
        isOpen={request?.metricKey === 'item'}
        title={titleFor('item', '수행업무 목록')}
        onTaskItemClick={openTaskItemNode}
      />
      <TaskBudgetsListPanel
        {...commonProps}
        isOpen={request?.metricKey === 'budget'}
        title={titleFor('budget', '예산 목록')}
        onBudgetItemClick={openTask}
      />
      <TaskPerformancesListPanel
        {...commonProps}
        isOpen={request?.metricKey === 'performance'}
        title={titleFor('performance', '성과 목록')}
        onPerformanceItemClick={openTask}
      />
      <TaskDetailPanel
        isOpen={detail != null}
        onClose={() => setDetail(null)}
        taskId={detail?.id ?? null}
        taskType={detail?.type ?? null}
      />
    </>
  )
}
